import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Role } from '@lov/shared';
import type { PaginatedVenuesResponse } from '@lov/shared';
import { useAuth } from './AuthContext';
import { validatedApi } from '../lib/api/validated-client';
import type { VenueResponse } from '../features/venues/hooks/useVenues';

const STORAGE_KEY = 'lov-venue-context';
/** Persisted hint so Suspense fallbacks can show the correct themed animation. */
const VENUE_TYPE_HINT_KEY = 'lov-venue-type-hint';

interface VenueContextType {
  selectedVenueId: string | 'all';
  setSelectedVenue: (id: string | 'all') => void;
  venues: VenueResponse[];
  isLoading: boolean;
  isStaff: boolean;
}

export const VenueContext = createContext<VenueContextType | undefined>(undefined);

interface VenueProviderProps {
  children: ReactNode;
}

export function VenueProvider({ children }: VenueProviderProps) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const isVendorRole = user?.role === Role.VENDOR || user?.role === Role.VENDOR_STAFF;
  const isStaff = user?.role === Role.VENDOR_STAFF;

  // Only fetch venues for vendor roles to avoid unnecessary API calls for other users
  const { data: venues = [], isLoading } = useQuery<
    PaginatedVenuesResponse,
    Error,
    VenueResponse[]
  >({
    queryKey: ['venues', 'my'],
    queryFn: async () => {
      return validatedApi.get('/venues/my');
    },
    select: (response) => response.data,
    enabled: isVendorRole,
  });

  const [selectedVenueId, setSelectedVenueId] = useState<string | 'all'>(() => {
    if (!isVendorRole) return 'all';
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) return stored;
    } catch {
      // Ignore storage errors
    }
    return 'all';
  });

  // Auto-select logic: single venue -> auto-select, multi -> 'all', stale ID -> 'all'
  useEffect(() => {
    if (isLoading || !isVendorRole) return;

    if (venues.length === 1) {
      setSelectedVenueId(venues[0].id);
      try {
        localStorage.setItem(STORAGE_KEY, venues[0].id);
      } catch {
        // Ignore storage errors
      }
      return;
    }

    if (venues.length > 1) {
      // Validate stored ID still exists in current venues list
      if (selectedVenueId !== 'all') {
        const exists = venues.some((v) => v.id === selectedVenueId);
        if (!exists) {
          setSelectedVenueId('all');
          try {
            localStorage.setItem(STORAGE_KEY, 'all');
          } catch {
            // Ignore storage errors
          }
        }
      }
      return;
    }

    // No venues - default to 'all'
    setSelectedVenueId('all');
  }, [venues, isLoading, isVendorRole]);

  // Persist venue type hint to localStorage so Suspense fallbacks (rendered before
  // context resolves) can show the correct themed animation on subsequent visits.
  useEffect(() => {
    if (isLoading || !isVendorRole || venues.length === 0) return;

    const selectedVenue =
      selectedVenueId !== 'all' ? venues.find((v) => v.id === selectedVenueId) : null;
    const hint = selectedVenue?.venueType ?? venues[0]?.venueType;

    if (hint) {
      try {
        localStorage.setItem(VENUE_TYPE_HINT_KEY, hint);
      } catch {
        // Ignore storage errors
      }
    }
  }, [venues, selectedVenueId, isLoading, isVendorRole]);

  const setSelectedVenue = useCallback(
    (id: string | 'all') => {
      setSelectedVenueId(id);
      try {
        localStorage.setItem(STORAGE_KEY, id);
      } catch {
        // Ignore storage errors
      }
      // Invalidate vendor/analytics-scoped queries for the new venue
      queryClient.invalidateQueries({ queryKey: ['vendor'] });
      queryClient.invalidateQueries({ queryKey: ['vendor-analytics'] });
      queryClient.invalidateQueries({ queryKey: ['payouts', 'vendor'] });
      queryClient.invalidateQueries({ queryKey: ['vendor-daily-sales'] });
    },
    [queryClient]
  );

  // For non-vendor roles, just render children with a no-op context
  if (!isVendorRole) {
    return (
      <VenueContext.Provider
        value={{
          selectedVenueId: 'all',
          // eslint-disable-next-line @typescript-eslint/no-empty-function
          setSelectedVenue: () => {},
          venues: [],
          isLoading: false,
          isStaff: false,
        }}
      >
        {children}
      </VenueContext.Provider>
    );
  }

  return (
    <VenueContext.Provider
      value={{
        selectedVenueId,
        setSelectedVenue,
        venues,
        isLoading,
        isStaff,
      }}
    >
      {children}
    </VenueContext.Provider>
  );
}

export function useVenueContext(): VenueContextType {
  const context = useContext(VenueContext);
  if (context === undefined) {
    throw new Error('useVenueContext must be used within a VenueProvider');
  }
  return context;
}
